import { useState } from 'react';
import { Bell, Send, Users, Store, Truck, Globe, CheckCircle } from '../../components/icons';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { extractErr } from '../../api/error-message';
import { PageHeader } from '../../components/page-header';
import { useConfirm } from '../../components/confirm-dialog';

// Wired to POST /admin/notifications/broadcast. Sends an in-app notification
// (and push, when FCM is configured) to a whole audience. Scheduling and
// reusable templates are future work.

type Audience = 'CUSTOMERS' | 'SELLERS' | 'DELIVERY' | 'ALL';

const AUDIENCES: { key: Audience; label: string; icon: React.ReactNode }[] = [
  { key: 'CUSTOMERS', label: 'Customers', icon: <Users size={16} /> },
  { key: 'SELLERS', label: 'Sellers', icon: <Store size={16} /> },
  { key: 'DELIVERY', label: 'Delivery partners', icon: <Truck size={16} /> },
  { key: 'ALL', label: 'Everyone', icon: <Globe size={16} /> },
];

export function NotificationsPage() {
  const { confirm } = useConfirm();
  const [audience, setAudience] = useState<Audience>('CUSTOMERS');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [lastSent, setLastSent] = useState<{ audience: Audience; recipients: number; title: string } | null>(null);

  const send = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Title and message are required');
      return;
    }
    const audienceLabel = AUDIENCES.find((a) => a.key === audience)?.label ?? audience;
    const ok = await confirm({
      title: `Send to ${audience === 'ALL' ? 'everyone' : `all ${audienceLabel.toLowerCase()}`}?`,
      description: `“${title.trim()}” goes out straight away as an in-app notification (and push, where set up). A sent broadcast can’t be recalled.`,
      confirmLabel: 'Send broadcast',
      tone: 'primary',
    });
    if (!ok) return;
    setSending(true);
    try {
      const { data } = await api.post<{ recipients: number }>('/admin/notifications/broadcast', {
        audience,
        title: title.trim(),
        body: body.trim(),
      });
      toast.success(`Sent to ${data.recipients} recipient${data.recipients === 1 ? '' : 's'}`);
      setLastSent({ audience, recipients: data.recipients, title: title.trim() });
      setTitle('');
      setBody('');
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Notifications" description="Broadcast an announcement to an audience" />

      <div className="panel max-w-2xl space-y-5 p-6">
        <div>
          <p className="field-label mb-2" id="audience-label">Audience</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="group" aria-labelledby="audience-label">
            {AUDIENCES.map((a) => (
              <button
                key={a.key}
                type="button"
                onClick={() => setAudience(a.key)}
                aria-pressed={audience === a.key}
                className={`flex h-11 items-center gap-2 rounded-sm border px-3 text-[14px] font-semibold transition-colors ${
                  audience === a.key ? 'border-admin bg-admin-soft text-admin-ink' : 'border-surface-line-strong bg-surface-card text-ink-700 hover:bg-surface-2'
                }`}
              >
                {a.icon} {a.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="field-label mb-1.5" htmlFor="broadcast-title">Title</label>
          <input
            id="broadcast-title"
            type="text"
            value={title}
            maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Festive sale starts tomorrow!"
            className="input"
          />
        </div>

        <div>
          <label className="field-label mb-1.5" htmlFor="broadcast-body">Message</label>
          <textarea
            id="broadcast-body"
            rows={4}
            value={body}
            maxLength={1000}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write the announcement your audience will see…"
            className="input resize-none"
          />
          <p className="field-help mt-1 text-right font-tabular">{body.length}/1000</p>
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-1">
          <button
            type="button"
            disabled={sending}
            onClick={() => void send()}
            className="btn btn-primary"
          >
            <Send size={18} /> {sending ? 'Sending…' : 'Send broadcast'}
          </button>
          <span className="flex items-center gap-1.5 text-[13.5px] text-ink-500">
            <Bell size={16} /> In-app now · push when FCM is configured
          </span>
        </div>
      </div>

      {lastSent && (
        <div className="alert alert-success max-w-2xl items-center">
          <CheckCircle size={20} />
          <p>
            Last broadcast <span className="font-semibold">“{lastSent.title}”</span> reached{' '}
            <span className="font-semibold">{lastSent.recipients}</span>{' '}
            {AUDIENCES.find((a) => a.key === lastSent.audience)?.label.toLowerCase()}.
          </p>
        </div>
      )}
    </div>
  );
}
